import React, { Component } from "react";
import { Button, Alert, Form } from "react-bootstrap";
import configData from "../../../config.json";

export default class FileUploadStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDragOver: false,
      uploading: false,
      sessionName: "",
      selectedFile: null,
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    // If we already have session data, show it
    if (this.props.session.sessionId) {
      this.setState({
        sessionName: this.props.session.sessionName || this.props.session.fileName,
      });
    }
  }

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    this.setState({ isDragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDragOver: false });
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      this.handleFileSelect(files[0]);
    }
  };

  handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.handleFileSelect(file);
    }
  };

  handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    const allowedExtensions = ['.xls', '.xlsx', '.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      this.props.onError("Please select a valid Excel (.xls, .xlsx) or CSV file");
      return;
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      this.props.onError("File size must be less than 50MB");
      return;
    }

    this.setState({ 
      selectedFile: file,
      sessionName: this.state.sessionName || file.name.substring(0, file.name.lastIndexOf('.'))
    });
  };

  uploadFile = () => {
    const { selectedFile, sessionName } = this.state;
    
    if (!selectedFile) {
      this.props.onError("Please select a file to upload");
      return;
    }

    if (!sessionName.trim()) {
      this.props.onError("Please provide a session name");
      return;
    }

    this.setState({ uploading: true });

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("session_name", sessionName.trim());
    // Authentication from sessionStorage (existing user session)
    formData.append("sId", sessionStorage.getItem("sessionId")); // User's encrypted session ID
    formData.append("uId", sessionStorage.getItem("uId")); // User's ID

    fetch(configData.api_url + "C_BulkImport/upload", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((json) => {
        this.setState({ uploading: false });
        
        if (json.status === "success") {
          this.props.onSuccess("File uploaded successfully!");
          this.props.onUpdate({
            sessionId: json.session_id, // Import session ID (DB record), NOT auth session
            sessionCode: json.session_code,
            sessionName: sessionName,
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            fileType: json.file_info.type,
            totalRows: json.total_rows,
            headers: json.headers,
            status: "uploaded",
          });
          
          // Auto-proceed to next step
          setTimeout(() => {
            this.props.onNext();
          }, 1000);
        } else {
          this.props.onError(json.message || "Upload failed");
        }
      })
      .catch((error) => {
        console.error("Upload error:", error);
        this.setState({ uploading: false });
        this.props.onError("Network error occurred during upload");
      });
  };

  formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  triggerFileInput = () => {
    this.fileInputRef.current?.click();
  };

  render() {
    const { isDragOver, uploading, sessionName, selectedFile } = this.state;
    const { session } = this.props;
    
    // If session already exists, show the uploaded file info
    if (session.sessionId && !selectedFile) {
      return (
        <div className="text-center py-5">
          <div className="file-info">
            <div className="file-info-header">
              <div className="file-icon">
                <i className="fas fa-file-excel"></i>
              </div>
              <div className="file-details">
                <h5>{session.fileName}</h5>
                <p>
                  {this.formatFileSize(session.fileSize)} • {session.fileType?.toUpperCase()} • {session.totalRows} rows
                </p>
              </div>
            </div>
            
            <Alert variant="success" className="mt-3">
              <i className="fas fa-check-circle me-2"></i>
              File successfully uploaded as session <strong>{session.sessionCode}</strong>
            </Alert>
            
            <Button
              variant="outline-primary"
              onClick={() => {
                this.setState({ selectedFile: null });
                this.props.onUpdate({
                  sessionId: null,
                  sessionCode: null,
                  sessionName: "",
                  fileName: "",
                  fileSize: 0,
                  fileType: "",
                  totalRows: 0,
                  headers: [],
                  status: "uploaded",
                });
              }}
            >
              <i className="fas fa-upload me-2"></i>
              Upload Different File
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="mb-2">Upload Your Data File</h4>
            <p className="text-muted">
              Choose an Excel (.xlsx, .xls) or CSV file containing the data you want to import.
              The first row should contain column headers.
            </p>
          </div>
        </div>

        {/* Session Name Input */}
        <div className="row mb-4">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>Session Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter a descriptive name for this import"
                value={sessionName}
                onChange={(e) => this.setState({ sessionName: e.target.value })}
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                This helps you identify the import later
              </Form.Text>
            </Form.Group>
          </div>
        </div>

        {/* File Upload Area */}
        <div
          className={`upload-area ${isDragOver ? "dragover" : ""}`}
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
          onClick={this.triggerFileInput}
        >
          <div className="upload-icon">
            <i className="fas fa-cloud-upload-alt"></i>
          </div>
          <div className="upload-text">
            {selectedFile ? selectedFile.name : "Drag & drop your file here"}
          </div>
          <div className="upload-subtext">
            or click to browse files
          </div>
          <div className="mt-3 text-muted small">
            <strong>Supported formats:</strong> .xlsx, .xls, .csv<br />
            <strong>Maximum size:</strong> 50MB
          </div>
        </div>

        <input
          ref={this.fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={this.handleFileInputChange}
          style={{ display: "none" }}
        />

        {/* Selected File Info */}
        {selectedFile && (
          <div className="file-info">
            <div className="file-info-header">
              <div className="file-icon">
                <i className="fas fa-file-excel"></i>
              </div>
              <div className="file-details">
                <h5>{selectedFile.name}</h5>
                <p>
                  {this.formatFileSize(selectedFile.size)} • 
                  {selectedFile.name.toLowerCase().endsWith('.csv') ? ' CSV' : ' Excel'}
                </p>
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <Button
                variant="primary"
                onClick={this.uploadFile}
                disabled={uploading || !sessionName.trim()}
                className="btn-primary-custom"
              >
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload me-2"></i>
                    Upload File
                  </>
                )}
              </Button>
              
              <Button
                variant="outline-secondary"
                onClick={() => {
                  this.setState({ selectedFile: null, sessionName: "" });
                  this.fileInputRef.current.value = "";
                }}
                disabled={uploading}
              >
                <i className="fas fa-times me-2"></i>
                Remove
              </Button>
            </div>
          </div>
        )}

        {/* Help Information */}
        <div className="row mt-5">
          <div className="col-12">
            <Alert variant="info">
              <h6 className="alert-heading">
                <i className="fas fa-lightbulb me-2"></i>
                Tips for Best Results
              </h6>
              <ul className="mb-0">
                <li>Ensure the first row contains clear column headers</li>
                <li>Remove any empty rows or columns at the beginning</li>
                <li>Use consistent date formats (YYYY-MM-DD recommended)</li>
                <li>Avoid special characters in text fields when possible</li>
                <li>For large files, consider splitting into smaller batches</li>
              </ul>
            </Alert>
          </div>
        </div>
      </div>
    );
  }
}